import { CreditCard, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  describeError,
  useBillingStatus,
  useOpenBillingPortal,
  useStartCheckout,
  type BillingStatus,
  type EntitlementReason,
} from "@/lib/api";

// The one price, written once. Everything the driver reads about cost comes
// from here, so the app can never quote two different numbers.
export const PRICE_LABEL = "$9.99/month";

// --- Subscribe ------------------------------------------------------------

// The only button in the app that touches money — and all it does is ask the
// server for a URL and follow it. The card is entered on Stripe's page.
export function SubscribeButton({
  label = "Subscribe",
  className,
  size = "default",
}: {
  label?: string;
  className?: string | undefined;
  size?: "default" | "lg";
}) {
  const checkout = useStartCheckout();

  return (
    <div className="space-y-2">
      <Button
        size={size}
        className={className}
        disabled={checkout.isPending}
        onClick={() => checkout.mutate()}
      >
        {checkout.isPending ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Opening secure checkout…
          </>
        ) : (
          <>
            <CreditCard className="h-4 w-4" />
            {label}
          </>
        )}
      </Button>
      {checkout.isError && (
        <p className="text-xs text-destructive" role="alert">
          Couldn&apos;t open checkout: {describeError(checkout.error)}
        </p>
      )}
    </div>
  );
}

// --- Manage billing -------------------------------------------------------

// Shown only to someone who has a Stripe customer. Cancelling, changing the
// card, and invoices all live on Stripe's portal — this is a link to it.
export function ManageBillingLink() {
  const portal = useOpenBillingPortal();

  return (
    <button
      type="button"
      className="text-xs underline text-muted-foreground disabled:opacity-60"
      disabled={portal.isPending}
      onClick={() => portal.mutate()}
    >
      {portal.isPending ? "Opening…" : "Manage billing"}
    </button>
  );
}

// --- The trial strip ------------------------------------------------------

// Sits at the top of every signed-in screen. Four states and no more:
//
//   comp      → a quiet "no billing" note; never a countdown, never a paywall
//   trialing  → a countdown with a way to subscribe before it runs out
//   blocked   → the paywall, in place, with viewing still working below it
//   active    → nothing but a quiet Manage billing link
//
// It renders NOTHING while the status is loading or errored: a billing strip
// that flickers in on every page load would be worse than no strip, and a
// failed status read must not imply the driver has been cut off.
export function BillingBanner() {
  const { data } = useBillingStatus();
  if (!data) return null;

  if (data.allowed === false) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <PaywallCard status={data} compact />
      </div>
    );
  }

  // Checked before `trialing`, because a comped org can still be sitting on
  // a `trialing` row — comp overrides the status rather than replacing it,
  // and counting down a trial that will never cut them off would be a lie.
  if (data.reason === "comp") {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-4 flex justify-end">
        <p className="text-xs text-muted-foreground">Comped account — no billing</p>
      </div>
    );
  }

  if (data.reason === "trialing") {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-4">
        <Card className="p-3 flex flex-wrap items-center justify-between gap-3 border-amber-400/60 bg-amber-50/60 dark:bg-amber-950/20">
          <p className="text-sm">
            <span className="font-medium">{daysLeftLabel(data.daysLeft)}</span> left in your
            free trial
          </p>
          {data.configured && <SubscribeButton label="Subscribe" />}
        </Card>
      </div>
    );
  }

  if (data.manageable) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-4 flex justify-end">
        <ManageBillingLink />
      </div>
    );
  }

  return null;
}

// "1 day" / "6 days", never "0 days" — the server rounds part-days up, and
// the last day of a trial should read as a day, not as nothing.
export function daysLeftLabel(daysLeft: number | null): string {
  const n = daysLeft ?? 0;
  return `${n} ${n === 1 ? "day" : "days"}`;
}

// --- The paywall ----------------------------------------------------------

// Why they're blocked, in the driver's words. Every branch ends somewhere
// they can act, because a dead end here is a lost customer.
export function paywallHeadline(reason: EntitlementReason): string {
  switch (reason) {
    // Unreachable in practice — a comped org is always allowed, so the
    // paywall never renders for one. Given its own sentence anyway rather
    // than falling through to "your trial has ended", which would be a
    // flatly wrong thing to tell someone we comped.
    case "comp":
      return "Your account is comped.";
    case "trial_expired":
      return "Your free trial has ended.";
    case "canceled":
      return "Your subscription was cancelled.";
    default:
      return "Your subscription isn't active.";
  }
}

// The screen a blocked driver sees where the paid action used to be. `compact`
// is the banner form at the top of the app; the full form takes over the
// capture screen, where the block is what they came to do.
export function PaywallCard({
  status,
  compact = false,
}: {
  status: Pick<BillingStatus, "reason" | "manageable" | "configured">;
  compact?: boolean;
}) {
  return (
    <Card
      className={
        (compact ? "p-4" : "p-6") +
        " space-y-3 border-primary/40 bg-primary/5"
      }
      role="alert"
    >
      <div className="flex items-start gap-3">
        <Lock className="h-5 w-5 text-primary shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-medium">{paywallHeadline(status.reason)}</p>
          <p className="text-sm text-muted-foreground">
            Keep logging your pay for <span className="font-semibold">{PRICE_LABEL}</span>.
          </p>
        </div>
      </div>

      {status.configured ? (
        <SubscribeButton
          label="Subscribe"
          size={compact ? "default" : "lg"}
          className={compact ? undefined : "w-full h-12 text-base"}
        />
      ) : (
        <p className="text-sm text-destructive">
          Subscriptions aren&apos;t available on this server yet.
        </p>
      )}

      {/* Said plainly, because both are the questions a driver actually has
          before they will type a card number. */}
      <p className="text-xs text-muted-foreground">
        Cancel anytime. Your data stays.
      </p>

      {status.manageable && <ManageBillingLink />}
    </Card>
  );
}
